import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import useQuery from 'hooks/useQuery'
import {
  filterEmptyValues,
  getDepartmentOptions,
  getFamiliesOptionsBySection,
  getSectionOptionsByDepartment,
} from 'utils/helpers'
import { Button, Dropdown, TextInput } from 'components/common'
import { MASTER_DATA_FORM_FIELDS, VAT_CLASS_OPTIONS } from 'utils/constants'

const {
  BARCODE,
  DEPARTMENT,
  FAMILY,
  SECTION,
  SUB_FAMILY,
  VAT_CLASS,
  ACTIVE_ONLINE,
  IS_DISABLE,
} = MASTER_DATA_FORM_FIELDS

type Props = {
  toggleFilterForm: () => void
}

export const FILTER_FIELD_BUNDLE_PRODUCT = 'bundleProductFilters'

const yesNoOptions = [
  { label: '--- NONE ---', value: 'NONE', disabled: false },
  { label: 'Yes', value: 'true', disabled: false },
  { label: 'No', value: 'false', disabled: false },
]

const vatClassFilterOptions = [
  { label: '--- NONE ---', value: 'NONE', disabled: false },
  ...VAT_CLASS_OPTIONS,
]

const BundleProductFilterForm = ({ toggleFilterForm }: Props) => {
  const { getQuery, removeQuery, setMultipleQueries } = useQuery()
  const filters = getQuery(FILTER_FIELD_BUNDLE_PRODUCT)

  const [resetFilter, setResetFilter] = useState(false)

  const { register, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: filters,
  })

  const onFilterApply = (data: any) => {
    if (data?.dept === 'NONE') delete data.dept
    if (data?.section === 'NONE') delete data.section
    if (data?.family === 'NONE') delete data.family
    if (data?.vatClass === 'NONE') delete data.vatClass
    if (data?.activeOnline === 'NONE') delete data.activeOnline
    if (data?.isDisable === 'NONE') delete data.isDisable

    const filteredData = filterEmptyValues(data)

    if (Object.values(filteredData).length > 0) {
      setMultipleQueries({
        [FILTER_FIELD_BUNDLE_PRODUCT]: filteredData,
        page: 1,
      })
    } else {
      removeQuery(FILTER_FIELD_BUNDLE_PRODUCT)
    }

    toggleFilterForm()
  }

  const deptWatch = watch('dept')
  const sectionWatch = watch('section')

  const deptOptions = useMemo(() => getDepartmentOptions(), [])

  const sectionOptions = useMemo(() => {
    const dept = deptWatch || deptOptions[0]?.value
    return getSectionOptionsByDepartment(dept)
    // eslint-disable-next-line
  }, [deptWatch])

  const familyOptions = useMemo(() => {
    const section = sectionWatch || sectionOptions[0]?.value
    return getFamiliesOptionsBySection(section)
    // eslint-disable-next-line
  }, [sectionWatch, deptWatch])

  const cancelFilter = () => {
    reset(filters)
    setResetFilter((prev) => !prev)
    toggleFilterForm()
  }

  return (
    <div className="mt-2 rounded-lg border bg-white px-5 py-3">
      <div className="mx-auto max-w-5xl">
        <form onSubmit={handleSubmit(onFilterApply)}>
          <div className="grid grid-cols-2 gap-x-14 gap-y-3 md:grid-cols-3">
            <TextInput
              register={register}
              name={BARCODE.key}
              label={BARCODE.label}
            />

            <Dropdown
              name={DEPARTMENT.key}
              label={DEPARTMENT.label}
              options={deptOptions}
              setValue={setValue}
              defaultValue={filters?.dept || resetFilter}
            />

            <Dropdown
              name={SECTION.key}
              label={SECTION.label}
              options={sectionOptions}
              setValue={setValue}
              defaultValue={filters?.section || sectionOptions?.[1]}
            />

            <Dropdown
              name={FAMILY.key}
              label={FAMILY.label}
              options={familyOptions}
              setValue={setValue}
              defaultValue={filters?.family || familyOptions?.[1]}
            />

            <TextInput
              name={SUB_FAMILY.key}
              label={SUB_FAMILY.label}
              register={register}
            />

            <Dropdown
              name={VAT_CLASS.key}
              label={VAT_CLASS.label}
              options={vatClassFilterOptions}
              setValue={setValue}
              defaultValue={filters?.vatClass || resetFilter}
            />

            <Dropdown
              name={ACTIVE_ONLINE.key}
              label={ACTIVE_ONLINE.label}
              setValue={setValue}
              defaultValue={filters?.[ACTIVE_ONLINE.key] || resetFilter}
              options={yesNoOptions}
            />

            <Dropdown
              name={IS_DISABLE.key}
              label={IS_DISABLE.label}
              setValue={setValue}
              defaultValue={filters?.[IS_DISABLE.key] || resetFilter}
              options={yesNoOptions}
            />
          </div>
          <div className="my-4 flex gap-5">
            <Button buttonText="Apply Filters" className="w-32" />

            <Button
              buttonText="cancel"
              variant="outline"
              type="button"
              onClick={cancelFilter}
              className="w-32"
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export default BundleProductFilterForm
